import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Card, TextField, MenuItem, Button, 
    Typography, IconButton, Snackbar, Alert, Grid
} from "@mui/material";
import {
    DeleteOutline as DeleteIcon,
    Refresh as RefreshIcon,
    UploadFile as UploadIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { useNavigate } from 'react-router-dom';
import ManualAdhocModal from './ManualAdhocModal';

const AdhocVariablesList = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
    });
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });
    const [modalOpen, setModalOpen] = useState(false);

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const years = [2025, 2026, 2027];

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/adhoc`, {
                params: {
                    payroll_month: filters.month,
                    payroll_year: filters.year
                },
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setRowData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching adhoc data:", error);
            setStatusMsg({ open: true, msg: 'Error fetching data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this ad-hoc record?")) return;
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/payroll/adhoc/${id}`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (res.data.success) {
                setStatusMsg({ open: true, msg: 'Record deleted', severity: 'success' });
                fetchData();
            }
        } catch (error) {
            setStatusMsg({ open: true, msg: 'Error deleting record', severity: 'error' });
        }
    };

    const columnDefs = useMemo(() => [
        { field: "employee_number", headerName: "Emp. No", width: 120, filter: true },
        { field: "full_name", headerName: "Staff Name", flex: 1, filter: true },
        { field: "element_code", headerName: "Element Code", width: 130, filter: true },
        { field: "element_name", headerName: "Element Name", width: 180, filter: true },
        { 
            field: "element_type", 
            headerName: "Type", 
            width: 120,
            cellRenderer: params => {
                const color = params.value === 'addition' ? '#2e7d32' : params.value === 'deduction' ? '#d32f2f' : '#1976d2';
                return <span style={{ color, fontWeight: 'bold' }}>{params.value?.toUpperCase()}</span>;
            }
        },
        { 
            field: "amount", 
            headerName: "Amount", 
            width: 140,
            valueFormatter: p => p.value?.toLocaleString(),
            cellStyle: { fontWeight: 'bold' }
        },
        { field: "created_at", headerName: "Imported At", width: 160, valueFormatter: p => new Date(p.value).toLocaleString() },
        {
            headerName: "Actions",
            width: 100,
            cellRenderer: (params) => (
                <IconButton color="error" size="small" onClick={() => params.context.handleDelete(params.data.id)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )
        }
    ], []);

    return (
        <Box sx={{ p: 0, backgroundColor: '#f5f7fb', minHeight: '100vh' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>
                        Ad-Hoc Variables
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setModalOpen(true)}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Add Single Record
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<UploadIcon />}
                            onClick={() => navigate('/payroll/adhoc-import')}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Import Ad-Hoc Data
                        </Button>
                    </Box>
                </Box>

                <ManualAdhocModal 
                    open={modalOpen} 
                    onClose={() => setModalOpen(false)} 
                    onSuccess={() => {
                        setStatusMsg({ open: true, msg: 'Record added successfully', severity: 'success' });
                        fetchData();
                    }}
                    initialMonth={filters.month}
                    initialYear={filters.year}
                />

                <Card sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Payroll Month"
                                value={filters.month}
                                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                            >
                                {months.map(m => (
                                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Payroll Year"
                                value={filters.year}
                                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                            >
                                {years.map(y => (
                                    <MenuItem key={y} value={y}>{y}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <Button 
                                variant="outlined" 
                                startIcon={<RefreshIcon />}
                                onClick={fetchData}
                                sx={{ borderRadius: 2 }}
                            >
                                Refresh
                            </Button>
                        </Grid>
                    </Grid>
                </Card>

                <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    <div className="ag-theme-quartz" style={{ height: 600, width: '100%' }}>
                        <AgGridReact
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={{ resizable: true, sortable: true }}
                            rowSelection="multiple"
                            animateRows={true}
                            loading={loading}
                            context={{ handleDelete }}
                        />
                    </div>
                </Card>
            </Box>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={4000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={statusMsg.severity} variant="filled">
                    {statusMsg.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdhocVariablesList;
